/**
 * NetworkWithAscii - Organic network growth with ASCII rendering
 */

import { AsciiBuffer } from './AsciiBuffer'
import { Node, GrowthState } from './Node'
import { SpatialHash } from './SpatialHash'
import { Embedding2D } from './embeddings'

// Markov chain transition matrix for snake-like movement
type TransitionMatrix = Record<GrowthState, Record<GrowthState, number>>

const TRANSITION_MATRIX: TransitionMatrix = {
  [GrowthState.GROW_STRAIGHT]: {
    [GrowthState.GROW_STRAIGHT]: 0.7,
    [GrowthState.BRANCH_LEFT]: 0.1,
    [GrowthState.BRANCH_RIGHT]: 0.1,
    [GrowthState.BRANCH_BOTH]: 0.05,
    [GrowthState.PAUSE]: 0.05,
  },
  [GrowthState.BRANCH_LEFT]: {
    [GrowthState.GROW_STRAIGHT]: 0.4,
    [GrowthState.BRANCH_LEFT]: 0.4,
    [GrowthState.BRANCH_RIGHT]: 0.1,
    [GrowthState.BRANCH_BOTH]: 0.05,
    [GrowthState.PAUSE]: 0.05,
  },
  [GrowthState.BRANCH_RIGHT]: {
    [GrowthState.GROW_STRAIGHT]: 0.4,
    [GrowthState.BRANCH_LEFT]: 0.1,
    [GrowthState.BRANCH_RIGHT]: 0.4,
    [GrowthState.BRANCH_BOTH]: 0.05,
    [GrowthState.PAUSE]: 0.05,
  },
  [GrowthState.BRANCH_BOTH]: {
    [GrowthState.GROW_STRAIGHT]: 0.8,
    [GrowthState.BRANCH_LEFT]: 0.05,
    [GrowthState.BRANCH_RIGHT]: 0.05,
    [GrowthState.BRANCH_BOTH]: 0.05,
    [GrowthState.PAUSE]: 0.05,
  },
  [GrowthState.PAUSE]: {
    [GrowthState.GROW_STRAIGHT]: 0.7,
    [GrowthState.BRANCH_LEFT]: 0.1,
    [GrowthState.BRANCH_RIGHT]: 0.1,
    [GrowthState.BRANCH_BOTH]: 0.05,
    [GrowthState.PAUSE]: 0.05,
  },
}

export interface NetworkConfig {
  width: number
  height: number
  charWidth: number
  charHeight: number
  colors: Record<string, string>
  getNodeColor?: (id: string) => string | null
  isNodeNew?: (id: string) => boolean
}

export class NetworkWithAscii {
  private nodes: Node[] = []
  private edges: Array<{ from: Node; to: Node }> = []
  private asciiBuffer: AsciiBuffer
  private spatialHash: SpatialHash
  private config: NetworkConfig
  private targetPositions: Map<string, Embedding2D> = new Map()
  private spawnedEmbeddings: Set<string> = new Set()
  private frameCounter = 0
  private growthInterval = 1.5 // frames between node spawns
  private maxAge = 6000 // Much longer lifetime for persistent trails
  private maxNodes = 3000 // More nodes allowed
  private reachedTargetsMap: Map<string, { x: number; y: number; text: string; age: number }> = new Map()
  private stateTransitionInterval = 10 // Frames before considering state change
  private knnEdgePairs: Array<[string, string]> = []
  private knnRecomputeInterval = 20
  private knnOverlayEnabled = true
  private hoveredTargetId: string | null = null
  private textVisible = true
  private nodesVisible = true
  private selectedTargetIndex: number | null = null

  constructor(config: NetworkConfig) {
    this.config = config

    // Calculate grid dimensions
    const cols = Math.floor(config.width / config.charWidth)
    const rows = Math.floor(config.height / config.charHeight)

    this.asciiBuffer = new AsciiBuffer(cols, rows)
    this.spatialHash = new SpatialHash(50)
  }

  /** Update theme colors at runtime */
  setColors(colors: Record<string, string>): void {
    this.config.colors = colors
  }

  /**
   * Set growth interval (controls spawn speed)
   */
  setGrowthInterval(interval: number): void {
    this.growthInterval = Math.max(1, interval)
  }

  /** Enable/disable kNN overlay lines */
  setKnnOverlayEnabled(enabled: boolean): void {
    this.knnOverlayEnabled = enabled
  }

  /** Enable/disable text labels */
  setTextVisible(visible: boolean): void {
    this.textVisible = visible
  }

  /** Enable/disable nodes and trails */
  setNodesVisible(visible: boolean): void {
    this.nodesVisible = visible
  }

  /** Set selected target index for highlighting */
  setSelectedTargetIndex(index: number | null): void {
    this.selectedTargetIndex = index
    // Update hovered target to match selection
    if (index !== null) {
      const targetIds = Array.from(this.targetPositions.keys())
      this.hoveredTargetId = targetIds[index] || null
    }
  }

  /** Clear trails and edges (keeps targets) */
  clearTrails(): void {
    const cols = Math.floor(this.config.width / this.config.charWidth)
    const rows = Math.floor(this.config.height / this.config.charHeight)
    this.asciiBuffer = new AsciiBuffer(cols, rows)
    this.nodes = []
    this.edges = []
    this.spawnedEmbeddings.clear()
    this.reachedTargetsMap.clear()
    this.frameCounter = 0
  }

  /** Report nearest target to a point within a pixel threshold */
  getNearestTargetId(x: number, y: number, maxDistancePx: number = 32): string | null {
    let bestId: string | null = null
    let bestD = maxDistancePx * maxDistancePx
    this.targetPositions.forEach((t, id) => {
      const dx = t.x - x
      const dy = t.y - y
      const d = dx * dx + dy * dy
      if (d <= bestD) {
        bestD = d
        bestId = id
      }
    })
    return bestId
  }

  /** Set hovered target ID for rendering emphasis */
  setHoveredTarget(id: string | null): void {
    this.hoveredTargetId = id
  }

  /** Draw small markers for each target (distinguishes semantic nodes from trail points) */
  private drawTargetMarkers(ctx: CanvasRenderingContext2D): void {
    const { getNodeColor, colors } = this.config
    ctx.save()
    this.targetPositions.forEach((t, id) => {
      const color = (getNodeColor && id) ? (getNodeColor(id) || colors.primaryAccent) : colors.primaryAccent
      const r = this.hoveredTargetId === id ? 5 : 3
      ctx.beginPath()
      ctx.arc(t.x, t.y, r, 0, Math.PI * 2)
      ctx.fillStyle = color
      ctx.globalAlpha = 0.9
      ctx.fill()
      // outline to ensure visibility
      ctx.lineWidth = 1
      ctx.strokeStyle = colors.windowBorder || '#444'
      ctx.globalAlpha = 0.6
      ctx.stroke()
    })
    ctx.restore()
  }

  private wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
    const words = text.split(/\s+/)
    const lines: string[] = []
    let current = ''
    for (const w of words) {
      const test = current ? current + ' ' + w : w
      if (ctx.measureText(test).width <= maxWidth) {
        current = test
      } else {
        if (current) lines.push(current)
        current = w
      }
    }
    if (current) lines.push(current)
    return lines
  }

  private luminance(hex: string): number {
    try {
      const c = hex.replace('#', '')
      const r = parseInt(c.substring(0, 2), 16) / 255
      const g = parseInt(c.substring(2, 4), 16) / 255
      const b = parseInt(c.substring(4, 6), 16) / 255
      const sr = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4)
      const sg = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4)
      const sb = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4)
      return 0.2126 * sr + 0.7152 * sg + 0.0722 * sb
    } catch {
      return 0.5
    }
  }

  private getHighContrastPanelColors(): { bg: string; text: string; stroke: string } {
    const bgBase = this.config.colors.primaryBg || '#111111'
    const isLight = this.luminance(bgBase) > 0.5
    if (isLight) {
      return { bg: 'rgba(0,0,0,0.88)', text: '#ffffff', stroke: 'rgba(0,0,0,0.6)' }
    }
    return { bg: 'rgba(255,255,255,0.9)', text: '#111111', stroke: 'rgba(0,0,0,0.25)' }
  }

  /**
   * Transition node to new state based on Markov matrix
   */
  private transitionState(node: Node): void {
    // Only transition if node has been in current state long enough
    if (node.stateAge < this.stateTransitionInterval) {
      return
    }

    const currentState = node.state
    const probabilities = TRANSITION_MATRIX[currentState]

    // Pick new state based on probabilities
    const rand = Math.random()
    let cumulative = 0

    for (const [state, prob] of Object.entries(probabilities)) {
      cumulative += prob
      if (rand < cumulative) {
        node.state = state as GrowthState
        node.stateAge = 0
        break
      }
    }
  }

  /**
   * Update target positions from 2D embeddings and text chunks (INCREMENTAL)
   * Accepts optional chunkIds array to use stable IDs instead of indices
   */
  updateTargetPositionsFromEmbeddings(
    embeddings2D: Array<[number, number]>,
    textChunks: Array<{ text: string; size: number }>,
    chunkIds?: string[]
  ): void {
    // DON'T clear existing targets - keep them for incremental growth!
    // this.targetPositions.clear()

    embeddings2D.forEach((coords, i) => {
      if (i < textChunks.length) {
        // Use stable chunk ID if provided, otherwise fall back to index
        const id = chunkIds && chunkIds[i] ? chunkIds[i] : `chunk-${i}`

        // Only update if this chunk doesn't exist yet OR if position changed significantly
        const existing = this.targetPositions.get(id)
        if (!existing) {
          // New target - add it
          this.targetPositions.set(id, {
            x: coords[0],
            y: coords[1],
            text: textChunks[i].text,
            id: id,
          })
        } else {
          // Existing target - update position if changed significantly
          const dx = Math.abs(existing.x - coords[0])
          const dy = Math.abs(existing.y - coords[1])
          if (dx > 10 || dy > 10) {
            existing.x = coords[0]
            existing.y = coords[1]
            existing.text = textChunks[i].text
          }
        }
      }
    })

  }

  /**
   * Compute kNN edges between target positions to visualize semantic proximity
   */
  private computeKnnEdges(k: number = 3): void {
    const ids = Array.from(this.targetPositions.keys())
    const positions = ids.map((id) => this.targetPositions.get(id)!)
    const pairs: Array<[string, string]> = []
    const seen = new Set<string>()
    for (let i = 0; i < positions.length; i++) {
      const a = positions[i]
      const dists: Array<{ j: number; d: number }> = []
      for (let j = 0; j < positions.length; j++) {
        if (i === j) continue
        const b = positions[j]
        const dx = a.x - b.x
        const dy = a.y - b.y
        dists.push({ j, d: dx * dx + dy * dy })
      }
      dists.sort((p, q) => p.d - q.d)
      const limit = Math.min(k, dists.length)
      for (let t = 0; t < limit; t++) {
        const j = dists[t].j
        const idA = ids[i]
        const idB = ids[j]
        const key = idA < idB ? `${idA}|${idB}` : `${idB}|${idA}`
        if (!seen.has(key)) {
          seen.add(key)
          pairs.push([idA, idB])
        }
      }
    }
    // Limit edges to avoid overdraw
    this.knnEdgePairs = pairs.slice(0, 500)
  }

  /**
   * Spawn new origin nodes (DISTRIBUTED across canvas like reference viz)
   */
  private spawnNodesForNewEmbeddings(): void {
    const { width, height } = this.config

    this.targetPositions.forEach((target, id) => {
      if (this.spawnedEmbeddings.has(id)) return
      if (this.nodes.length >= this.maxNodes) return

      let x: number, y: number, angle: number
      let spawnType = 'distributed'

      // Spawn origins DISTRIBUTED across entire canvas (not centered!)
      // This creates the spread-out look from the start
      if (this.nodes.length < 5) {
        // First few nodes: spawn from edges and corners
        const edge = Math.floor(Math.random() * 4)
        const padding = 40

        switch (edge) {
          case 0: // Top edge
            x = padding + Math.random() * (width - 2 * padding)
            y = padding
            break
          case 1: // Right edge
            x = width - padding
            y = padding + Math.random() * (height - 2 * padding)
            break
          case 2: // Bottom edge
            x = padding + Math.random() * (width - 2 * padding)
            y = height - padding
            break
          case 3: // Left edge
            x = padding
            y = padding + Math.random() * (height - 2 * padding)
            break
          default:
            x = width / 2
            y = height / 2
        }
      } else if (Math.random() < 0.9) {
        // 90% spawn near target positions (creates shorter paths)
        spawnType = 'near-target'
        const offsetRadius = 30 + Math.random() * 50 // 30-80px from target
        const offsetAngle = Math.random() * Math.PI * 2

        x = target.x + Math.cos(offsetAngle) * offsetRadius
        y = target.y + Math.sin(offsetAngle) * offsetRadius

        // Clamp to bounds
        x = Math.max(40, Math.min(width - 40, x))
        y = Math.max(40, Math.min(height - 40, y))
      } else {
        // 10% spawn randomly across canvas (creates varied paths)
        spawnType = 'random'
        const padding = 60
        x = padding + Math.random() * (width - 2 * padding)
        y = padding + Math.random() * (height - 2 * padding)
      }

      // Calculate angle toward target
      const baseAngle = Math.atan2(target.y - y, target.x - x)
      angle = baseAngle + (Math.random() - 0.5) * (Math.PI / 6)

      const origin = new Node(x, y, angle, id)
      origin.isActive = true
      origin.isOrigin = true

      this.nodes.push(origin)
      this.spatialHash.addNode(this.nodes.length - 1, x, y)
      this.spawnedEmbeddings.add(id)

    })
  }

  /** Manually spawn an origin near a coordinate aiming to nearest target */
  spawnManualOrigin(x: number, y: number): void {
    if (this.nodes.length >= this.maxNodes) return
    // Find nearest target
    let nearestId: string | null = null
    let bestD = Infinity
    this.targetPositions.forEach((t, id) => {
      const dx = t.x - x
      const dy = t.y - y
      const d = dx * dx + dy * dy
      if (d < bestD) {
        bestD = d
        nearestId = id
      }
    })
    const angle = nearestId
      ? Math.atan2(this.targetPositions.get(nearestId)!.y - y, this.targetPositions.get(nearestId)!.x - x)
      : Math.random() * Math.PI * 2
    const origin = new Node(x, y, angle, nearestId || null)
    origin.isActive = true
    origin.isOrigin = true
    this.nodes.push(origin)
    this.spatialHash.addNode(this.nodes.length - 1, x, y)
  }

  /**
   * Grow nodes toward targets with snake-like movement
   */
  private growTowardTargetsOneAtATime(): void {
    // Find active nodes that haven't reached their target
    const activeNodes = this.nodes.filter(
      (node) => node.isActive && !node.hasReachedTarget && node.id !== null
    )

    if (activeNodes.length === 0) return

    // Grow multiple nodes per frame for faster, denser trails
    const nodesToGrow = Math.min(8, activeNodes.length)

    for (let i = 0; i < nodesToGrow; i++) {
      const growingNode = activeNodes[Math.floor(Math.random() * activeNodes.length)]
      const target = this.targetPositions.get(growingNode.id!)

      if (!target) continue

      // Increment state age
      growingNode.stateAge++

      // Transition state if appropriate
      this.transitionState(growingNode)

      // Check if close to target
      const dx = target.x - growingNode.x
      const dy = target.y - growingNode.y
      const distance = Math.sqrt(dx * dx + dy * dy)

      if (distance < 10) {
        growingNode.hasReachedTarget = true
        growingNode.isActive = false

        // Store reached target permanently for persistent rendering
        if (growingNode.id && !this.reachedTargetsMap.has(growingNode.id)) {
          this.reachedTargetsMap.set(growingNode.id, {
            x: target.x,
            y: target.y,
            text: target.text,
            age: 0
          })
        }
        continue
      }

      // Handle BRANCH_BOTH specially (Y-split)
      if (growingNode.state === GrowthState.BRANCH_BOTH) {
        // Create TWO child nodes at ±36°
        const leftNode = new Node(
          growingNode.x,
          growingNode.y,
          growingNode.angle - Math.PI / 5, // -36°
          growingNode.id
        )
        leftNode.isActive = true
        leftNode.state = GrowthState.BRANCH_LEFT
        leftNode.birthTime = Date.now()

        const rightNode = new Node(
          growingNode.x,
          growingNode.y,
          growingNode.angle + Math.PI / 5, // +36°
          growingNode.id
        )
        rightNode.isActive = true
        rightNode.state = GrowthState.BRANCH_RIGHT
        rightNode.birthTime = Date.now()

        // Add both edges
        this.edges.push({ from: growingNode, to: leftNode })
        this.edges.push({ from: growingNode, to: rightNode })

        // Add both nodes
        this.nodes.push(leftNode, rightNode)
        this.spatialHash.addNode(this.nodes.length - 2, leftNode.x, leftNode.y)
        this.spatialHash.addNode(this.nodes.length - 1, rightNode.x, rightNode.y)

        // Deactivate parent after split
        growingNode.isActive = false
        continue
      }

      // Move the node with snake-like behavior
      growingNode.updateTowardTarget(target.x, target.y)

      // Create new child node at current position
      const newNode = new Node(growingNode.x, growingNode.y, growingNode.angle, growingNode.id)
      newNode.isActive = true
      newNode.state = growingNode.state // Inherit state
      newNode.birthTime = Date.now()

      // Add edge
      this.edges.push({ from: growingNode, to: newNode })

      // Add to nodes and spatial hash
      this.nodes.push(newNode)
      this.spatialHash.addNode(this.nodes.length - 1, newNode.x, newNode.y)

      // Keep parent active
    }
  }

  /**
   * Update node lifecycles and cull old nodes
   */
  private updateNodeLifecycles(): void {
    // Remove nodes that are too old
    const activeNodesCount = this.nodes.filter((n) => n.isActive).length

    this.nodes = this.nodes.filter((node) => {
      node.incrementAge()
      // Keep origin nodes and reached nodes MUCH longer (or forever)
      if (node.isOrigin || node.hasReachedTarget) {
        return true // Never remove reached targets or origins
      }
      // Keep regular nodes longer too
      return node.age < this.maxAge || activeNodesCount < 20
    })

    // Rebuild spatial hash occasionally (not every frame - expensive)
    if (this.frameCounter % 30 === 0) {
      this.spatialHash.rebuild(this.nodes)
    }

    // Clean up edges with removed nodes (less frequently)
    if (this.frameCounter % 60 === 0) {
      this.edges = this.edges.filter((edge) => {
        return this.nodes.includes(edge.from) && this.nodes.includes(edge.to)
      })
    }
  }

  /**
   * Main update loop (matches HybridViz pattern)
   */
  update(
    embeddings2D: Array<[number, number]>,
    textChunks: Array<{ text: string; size: number }>,
    deltaTime?: number,
    chunkIds?: string[]
  ): void {
    this.frameCounter++

    // Update target positions from embeddings (INCREMENTAL - only adds new targets)
    if (embeddings2D.length > 0 && textChunks.length > 0) {
      this.updateTargetPositionsFromEmbeddings(embeddings2D, textChunks, chunkIds)
    }

    // Recompute kNN edges periodically
    if (this.frameCounter % this.knnRecomputeInterval === 0) {
      this.computeKnnEdges(3)
    }

    // Spawn new origin nodes (organic pattern)
    this.spawnNodesForNewEmbeddings()

    // Grow nodes toward targets (every frame for faster movement)
    this.growTowardTargetsOneAtATime()

    // Update node lifecycles and cull old nodes
    this.updateNodeLifecycles()

    // Decay ASCII buffer for trailing effect (MUCH slower for persistent trails)
    this.asciiBuffer.decay(0.99) // Slower fade - trails last longer

    // Age reached targets
    this.reachedTargetsMap.forEach((target) => {
      target.age++
    })
  }

  /**
   * Draw to canvas via ASCII buffer
   */
  draw(ctx: CanvasRenderingContext2D, overlayCtx?: CanvasRenderingContext2D): void {
    const { charWidth, charHeight, colors, getNodeColor, isNodeNew } = this.config

    // Draw edges to ASCII buffer (BOLDER trails) - only if nodes visible
    if (this.nodesVisible) {
      this.edges.forEach((edge) => {
        const x0 = edge.from.x / charWidth
        const y0 = edge.from.y / charHeight
        const x1 = edge.to.x / charWidth
        const y1 = edge.to.y / charHeight

        // Stronger intensity for more visible trails
        const intensity = edge.to.isActive ? 1.0 : 0.85
        this.asciiBuffer.drawLine(x0, y0, x1, y1, intensity)
      })

      // Draw nodes to ASCII buffer with brightness boost for new nodes
      this.nodes.forEach((node) => {
        const x = node.x / charWidth
        const y = node.y / charHeight

        // Check if node is "new" from store (3 second window)
        const storeIsNew = isNodeNew && node.id ? isNodeNew(node.id) : false

        // New nodes pulse brighter (< 1 second old from birth, or from store)
        const age = Date.now() - node.birthTime
        const isNew = age < 1000 || storeIsNew
        const newNodeBoost = isNew ? Math.sin((age / 1000) * Math.PI) * 0.3 : 0

        const isHovered = this.hoveredTargetId && node.id === this.hoveredTargetId
        const radius = isHovered ? 3.2 : (node.isActive ? 1.8 : 1.2)
        const baseIntensity = isHovered ? 1.0 : (node.isActive ? 1.0 : 0.7)
        const intensity = Math.min(1.0, baseIntensity + newNodeBoost)

        this.asciiBuffer.drawPoint(x, y, radius, intensity)
      })
    }

    // Draw persistent text labels from reached targets - only if text visible
    if (this.textVisible) {
      const time = Date.now() * 0.003
      this.reachedTargetsMap.forEach((target, id) => {
      const x = target.x / charWidth
      const y = target.y / charHeight
      const cols = this.asciiBuffer.cols
      const rows = this.asciiBuffer.rows

      // Calculate max text length that fits on canvas from this position
      const maxCharsFromLeft = cols - Math.floor(x)
      const maxCharsFromRight = Math.floor(x)
      const maxChars = Math.max(maxCharsFromLeft, maxCharsFromRight, 30) // Minimum 30 chars

      // Truncate text only if necessary to fit within canvas bounds
      const text = target.text.length > maxChars ? target.text.slice(0, maxChars - 3) + '...' : target.text

      // Pulse effect for visual interest
      const pulse = Math.sin(time + target.x * 0.01) * 0.2 + 0.8

      // Draw a continuous point at the target to keep it visible
      this.asciiBuffer.drawPoint(x, y, 2.5, pulse)

      // Draw text label with bounds checking to prevent cutoff
      const textStartX = x - text.length / 2

      // Clamp text position to stay within canvas bounds
      let clampedX = textStartX
      if (clampedX < 0) {
        clampedX = 0
      } else if (clampedX + text.length > cols) {
        clampedX = cols - text.length
      }

      // Ensure clampedX is still valid after adjustment
      clampedX = Math.max(0, Math.min(cols - text.length, clampedX))

      // Clamp Y position too
      let clampedY = y + 2
      if (clampedY < 0) clampedY = 0
      if (clampedY >= rows) clampedY = rows - 1

        this.asciiBuffer.drawText(clampedX, clampedY, text, pulse * 0.9)
      })
    }

    // Render entire ASCII buffer to canvas with per-node colors (only if nodes visible)
    if (this.nodesVisible) {
      const defaultColor = (colors as any).vizTrail || colors.primaryAccent

      // Group nodes by color for efficient rendering
      const colorGroups = new Map<string, Node[]>()
      this.nodes.forEach(node => {
        const color = (getNodeColor && node.id) ? getNodeColor(node.id) : null
        const nodeColor = color || defaultColor
        if (!colorGroups.has(nodeColor)) {
          colorGroups.set(nodeColor, [])
        }
        colorGroups.get(nodeColor)!.push(node)
      })

      // Render buffer once with default color
      this.asciiBuffer.renderToCanvas(ctx, charWidth, charHeight, defaultColor)
    }

    // Draw kNN overlay (thin lines) on top of ASCII buffer
    if (this.knnOverlayEnabled && this.knnEdgePairs.length > 0) {
      ctx.save()
      ctx.lineWidth = 1
      ctx.globalAlpha = 0.12
      const overlayColor = (colors as any).vizOverlay || colors.secondaryText || '#888'
      ctx.strokeStyle = overlayColor
      ctx.beginPath()
      const w = this.config.width
      const h = this.config.height
      for (const [aId, bId] of this.knnEdgePairs) {
        const a = this.targetPositions.get(aId)
        const b = this.targetPositions.get(bId)
        if (!a || !b) continue
        const ax = Math.max(0, Math.min(w, a.x))
        const ay = Math.max(0, Math.min(h, a.y))
        const bx = Math.max(0, Math.min(w, b.x))
        const by = Math.max(0, Math.min(h, b.y))
        ctx.moveTo(ax, ay)
        ctx.lineTo(bx, by)
      }
      ctx.stroke()
      ctx.restore()
    }

    // Draw semantic target markers (distinct from ephemeral trail points)
    this.drawTargetMarkers(ctx)

    // Draw selection highlight ring
    if (this.selectedTargetIndex !== null) {
      const targetIds = Array.from(this.targetPositions.keys())
      const selectedId = targetIds[this.selectedTargetIndex]
      if (selectedId) {
        const target = this.targetPositions.get(selectedId)
        if (target) {
          ctx.save()
          ctx.beginPath()
          ctx.arc(target.x, target.y, 12, 0, Math.PI * 2)
          ctx.strokeStyle = colors.primaryAccent
          ctx.lineWidth = 2
          ctx.globalAlpha = 0.8
          ctx.stroke()
          ctx.restore()
        }
      }
    }

    // Hover label overlay for legibility (don't show in selection mode - selection panel shows the text)
    if (this.hoveredTargetId && this.selectedTargetIndex === null) {
      const target = this.targetPositions.get(this.hoveredTargetId)
      if (target) {
        const drawCtx = overlayCtx || ctx
        const padding = 8
        const maxWidth = Math.min(480, this.config.width - 20)
        const text = target.text
        drawCtx.save()
        // Use app-wide font (Departure Mono) with xs size (10px)
        drawCtx.font = '10px "Departure Mono", "Noto Emoji", "IBM Plex Mono", monospace'
        const lines = this.wrapText(drawCtx, text, maxWidth - padding * 2)
        const lineHeight = 14
        const clamped = lines.slice(0, 4)
        const widest = clamped.reduce((m, s) => Math.max(m, drawCtx.measureText(s).width), 0)
        const w = widest + padding * 2
        const h = clamped.length * lineHeight + padding * 2
        // Align to integer pixels for crispness
        const x = Math.round(Math.max(8, Math.min(this.config.width - w - 8, target.x + 12)))
        const y = Math.round(Math.max(8, Math.min(this.config.height - h - 8, target.y - (h + 12))))
        const hc = this.getHighContrastPanelColors()
        drawCtx.fillStyle = hc.bg
        drawCtx.strokeStyle = hc.stroke
        drawCtx.lineWidth = 1
        drawCtx.beginPath()
        drawCtx.rect(x, y, w, h)
        drawCtx.fill()
        drawCtx.stroke()
        drawCtx.fillStyle = hc.text
        drawCtx.textBaseline = 'top'
        let ty = y + padding
        for (const line of clamped) {
          drawCtx.fillText(line, Math.round(x + padding), Math.round(ty))
          ty += lineHeight
        }
        drawCtx.restore()
      }
    }
  }

  /**
   * Get statistics for debugging
   */
  getStats(): {
    nodes: number
    edges: number
    activeNodes: number
    reachedTargets: number
  } {
    return {
      nodes: this.nodes.length,
      edges: this.edges.length,
      activeNodes: this.nodes.filter((n) => n.isActive).length,
      reachedTargets: this.reachedTargetsMap.size, // Use persistent map count
    }
  }
}
